
import { Helmet } from "react-helmet";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Footer from "@/components/layout/Footer";
import ModernHeader from "@/components/cayman/ModernHeader";
import ModernHeroSection from "@/components/cayman/sections/ModernHeroSection";
import PostLaunchCTASection from "@/components/cayman/sections/PostLaunchCTASection";
import HowItWorksSection from "@/components/cayman/sections/HowItWorksSection";
import ModernFeatureCard from "@/components/cayman/ModernFeatureCard";
import { ArrowRight, BarChart3, ClipboardCheck, FileText, MessageCircle, ShieldCheck, Users } from "lucide-react";

const CaymanEnhanced2 = () => {
  // Update page title
  useEffect(() => {
    document.title = "NationCard™ Cayman Islands — The Pulse of a Nation";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>NationCard™ Cayman Islands — The Pulse of a Nation</title>
        <meta
          name="description"
          content="Experience the NationCard™ platform for the Cayman Islands - keeping democracy alive after election day."
        />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap" />
      </Helmet>

      {/* Header */}
      <ModernHeader />

      {/* Hero Section */}
      <ModernHeroSection />

      {/* Features Section */}
      <section id="features" className="py-24 px-4 bg-white dark:bg-gray-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white font-playfair">
              Features of NationCard™
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Discover how NationCard™ transforms civic engagement and brings transparency to governance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ModernFeatureCard
              title="Insight Cards"
              description="Access curated insights on government policies, promises, and performance with easy-to-understand cards."
              icon={FileText}
            />

            <ModernFeatureCard
              title="Community Polls"
              description="Participate in verified polls that reflect public opinion on important local issues affecting the Cayman Islands."
              icon={BarChart3}
            />

            <ModernFeatureCard
              title="Civic Discussion"
              description="Engage in moderated conversations about policies and issues that matter to the future of the Cayman Islands."
              icon={MessageCircle}
            />

            <ModernFeatureCard
              title="Verified Citizens"
              description="All participants are verified Cayman residents, ensuring authentic local perspectives and high-quality engagement."
              icon={ShieldCheck}
            />

            <ModernFeatureCard
              title="Community Action"
              description="Connect with fellow citizens to organize and drive meaningful change based on shared insights and priorities."
              icon={Users}
            />

            <ModernFeatureCard
              title="Accountability Tracking"
              description="Monitor promises and commitments made by elected officials with transparent performance metrics."
              icon={ClipboardCheck}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Process Section */}
      <section className="py-24 px-4 bg-white dark:bg-gray-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white font-playfair">
              The NationCard™ Process
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              A simple process to keep democracy alive between elections
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Step 1 */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all">
              <div className="text-5xl font-bold text-nation-purple mb-6 font-playfair">01</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Create an Account</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Sign up and verify your identity to join the NationCard™ community of engaged citizens.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all">
              <div className="text-5xl font-bold text-nation-purple mb-6 font-playfair">02</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Explore Insights</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Browse through curated cards about government initiatives, policies, and promises that affect you.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all">
              <div className="text-5xl font-bold text-nation-purple mb-6 font-playfair">03</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Make Your Voice Heard</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Participate in polls, share insights, and engage in meaningful discussions about Cayman's future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Post Launch CTA Section */}
      <PostLaunchCTASection />

      {/* Join Now Section */}
      <section id="join" className="py-24 px-4 bg-nation-blue text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-playfair">
            Join Hundreds of Engaged Cayman Citizens
          </h2>
          
          <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto leading-relaxed">
            Be part of a growing community that's reshaping civic engagement in the Cayman Islands.
          </p>
          
          <Button size="lg" className="bg-nation-purple hover:bg-nation-purple-dark shadow-lg hover:shadow-xl transition-all" asChild>
            <Link to="/cards/KY" className="px-10 py-6 text-lg flex items-center gap-2">
              Access NationCard™ Now <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CaymanEnhanced2;
